import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const FinancialDetails = ({ asset, isEditing, onUpdate, userRole = 'standard' }) => {
  const currencyOptions = [
    { value: 'inr', label: 'INR (₹)' },
    { value: 'usd', label: 'USD ($)' },
    { value: 'eur', label: 'EUR (€)' },
    { value: 'gbp', label: 'GBP (£)' },
    { value: 'cad', label: 'CAD ($)' }
  ];

  const depreciationMethodOptions = [
    { value: 'straight-line', label: 'Straight Line' },
    { value: 'declining-balance', label: 'Declining Balance' },
    { value: 'sum-of-years', label: 'Sum of Years Digits' },
    { value: 'units-of-production', label: 'Units of Production' }
  ];

  const handleFieldChange = (field, value) => {
    const updatedFinancial = { ...asset?.financialDetails, [field]: value };
    onUpdate({ ...asset, financialDetails: updatedFinancial });
  };

  const financial = asset?.financialDetails || {};
  const hasFinancialAccess = userRole === 'manager' || userRole === 'procurement' || userRole === 'admin';

  // Calculate depreciation
  const calculateDepreciation = () => {
    const purchasePrice = parseFloat(financial?.purchasePrice) || 0;
    const usefulLife = parseFloat(financial?.usefulLifeYears) || 5;
    const currentAge = financial?.purchaseDate ? 
      (new Date()?.getFullYear() - new Date(financial.purchaseDate)?.getFullYear()) : 0;
    
    if (financial?.depreciationMethod === 'straight-line') {
      const annualDepreciation = purchasePrice / usefulLife;
      const totalDepreciation = Math.min(annualDepreciation * currentAge, purchasePrice);
      return {
        annual: annualDepreciation,
        total: totalDepreciation,
        current: purchasePrice - totalDepreciation
      };
    }
    
    return { annual: 0, total: 0, current: purchasePrice };
  };

  const depreciation = calculateDepreciation();

  if (!hasFinancialAccess) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Icon name="Lock" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">
              Financial details are only available to managers and procurement staff.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      <h3 className="text-lg font-semibold text-foreground mb-6">Financial Details</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Purchase Information */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-foreground mb-3">Purchase Information</h4>
          
          <Input
            label="Purchase Price"
            type="number"
            value={financial?.purchasePrice || ''}
            onChange={(e) => handleFieldChange('purchasePrice', e?.target?.value)}
            disabled={!isEditing}
            placeholder="0.00"
            description="Original purchase cost"
          />

          <Select
            label="Currency"
            options={currencyOptions}
            value={financial?.currency || 'inr'}
            onChange={(value) => handleFieldChange('currency', value)}
            disabled={!isEditing}
          />

          <Input
            label="Purchase Order Number"
            type="text"
            value={financial?.purchaseOrderNumber || ''}
            onChange={(e) => handleFieldChange('purchaseOrderNumber', e?.target?.value)}
            disabled={!isEditing}
            placeholder="PO-2025-001"
          />

          <Input
            label="Vendor/Supplier"
            type="text"
            value={financial?.vendor || ''}
            onChange={(e) => handleFieldChange('vendor', e?.target?.value)}
            disabled={!isEditing}
            placeholder="Supplier name"
          />

          <Input
            label="Invoice Number"
            type="text"
            value={financial?.invoiceNumber || ''}
            onChange={(e) => handleFieldChange('invoiceNumber', e?.target?.value)}
            disabled={!isEditing}
            placeholder="INV-2025-001"
          />

          <Input
            label="Payment Method"
            type="text"
            value={financial?.paymentMethod || ''}
            onChange={(e) => handleFieldChange('paymentMethod', e?.target?.value)}
            disabled={!isEditing}
            placeholder="e.g., Corporate Credit Card"
          />
        </div>

        {/* Right Column - Depreciation & Current Value */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-foreground mb-3">Depreciation & Valuation</h4>
          
          <Select
            label="Depreciation Method"
            options={depreciationMethodOptions}
            value={financial?.depreciationMethod || 'straight-line'}
            onChange={(value) => handleFieldChange('depreciationMethod', value)}
            disabled={!isEditing}
          />

          <Input
            label="Useful Life (Years)"
            type="number"
            value={financial?.usefulLifeYears || ''}
            onChange={(e) => handleFieldChange('usefulLifeYears', e?.target?.value)}
            disabled={!isEditing}
            placeholder="5"
          />

          <Input
            label="Salvage Value"
            type="number"
            value={financial?.salvageValue || ''}
            onChange={(e) => handleFieldChange('salvageValue', e?.target?.value)}
            disabled={!isEditing}
            placeholder="0.00"
            description="Expected value at end of useful life"
          />

          {/* Calculated Values */}
          <div className="bg-muted rounded-lg p-4 space-y-3">
            <h5 className="text-sm font-medium text-foreground">Calculated Values</h5>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Annual Depreciation</p>
                <p className="font-medium text-foreground">
                  ₹{depreciation?.annual?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Total Depreciation</p>
                <p className="font-medium text-foreground">
                  ₹{depreciation?.total?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-muted-foreground">Current Book Value</p>
                <p className="font-semibold text-lg text-foreground">
                  ₹{depreciation?.current?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          <Input
            label="Insurance Value"
            type="number"
            value={financial?.insuranceValue || ''}
            onChange={(e) => handleFieldChange('insuranceValue', e?.target?.value)}
            disabled={!isEditing}
            placeholder="0.00"
            description="Current insurance replacement value"
          />
        </div>
      </div>
      {/* Budget & Cost Center */}
      <div className="mt-6">
        <h4 className="text-md font-medium text-foreground mb-4">Budget & Allocation</h4>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Input
            label="Cost Center"
            type="text"
            value={financial?.costCenter || ''}
            onChange={(e) => handleFieldChange('costCenter', e?.target?.value)}
            disabled={!isEditing}
            placeholder="CC-IT-001"
          />

          <Input
            label="Budget Code"
            type="text"
            value={financial?.budgetCode || ''}
            onChange={(e) => handleFieldChange('budgetCode', e?.target?.value)}
            disabled={!isEditing}
            placeholder="BUD-2025-IT"
          />

          <Input
            label="GL Account"
            type="text"
            value={financial?.glAccount || ''}
            onChange={(e) => handleFieldChange('glAccount', e?.target?.value)}
            disabled={!isEditing}
            placeholder="1200-IT-EQUIP"
          />
        </div>
      </div>
      {/* Financial Notes */}
      <div className="mt-6">
        <label className="block text-sm font-medium text-foreground mb-2">
          Financial Notes
        </label>
        <textarea
          value={financial?.financialNotes || ''}
          onChange={(e) => handleFieldChange('financialNotes', e?.target?.value)}
          disabled={!isEditing}
          rows={3}
          className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
          placeholder="Enter any financial notes or special considerations..."
        />
      </div>
    </div>
  );
};

export default FinancialDetails;